import React, { useState, useEffect } from 'react';
import { Save, X, Check, Ban, Lock, Calendar } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ListarHorariosResponse, DiaSemana, SalvarMarcacoesRequest } from '../../../types/horariosOficiais';
import { useToast } from '../../../hooks/use-toast';

const DIAS_SEMANA: { key: DiaSemana; label: string }[] = [
  { key: 'segunda', label: 'Segunda-feira' },
  { key: 'terca', label: 'Terça-feira' },
  { key: 'quarta', label: 'Quarta-feira' },
  { key: 'quinta', label: 'Quinta-feira' },
  { key: 'sexta', label: 'Sexta-feira' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' },
];

// Gerar horários de 09:00 a 23:00 em intervalos de 30min
function gerarHorariosDisponiveis(): string[] {
  const horarios: string[] = [];
  for (let h = 9; h <= 23; h++) {
    horarios.push(`${String(h).padStart(2, '0')}:00`);
    if (h < 23) {
      horarios.push(`${String(h).padStart(2, '0')}:30`);
    }
  }
  return horarios;
}

// Obter dia da semana atual
function getDiaAtual(): DiaSemana {
  const hoje = new Date().getDay(); // 0 (domingo) até 6 (sábado)
  const mapa: DiaSemana[] = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
  return mapa[hoje];
}

const HORARIOS_DISPONIVEIS = gerarHorariosDisponiveis();

interface CelulaHorarioProps {
  horario: string;
  marcado: boolean;
  editavel: boolean;
  onToggle: () => void;
}

function CelulaHorario({ horario, marcado, editavel, onToggle }: CelulaHorarioProps) {
  if (!editavel) {
    return (
      <td className="border-r border-b text-center p-0 min-w-[50px]">
        {marcado && (
          <div className="h-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center py-3">
            <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
          </div>
        )}
        {!marcado && <div className="h-full py-3"></div>}
      </td>
    );
  }

  return (
    <td className="border-r border-b text-center p-0 min-w-[50px]">
      <button
        onClick={onToggle}
        className={`w-full h-full py-3 transition-colors ${
          marcado
            ? 'bg-primary text-primary-foreground hover:bg-primary/80'
            : 'hover:bg-muted'
        }`}
      >
        {marcado && <Check className="w-4 h-4 mx-auto" />}
      </button>
    </td>
  );
}

interface CelulaAusenteProps {
  ausente: boolean;
  editavel: boolean;
  onToggle: () => void;
}

function CelulaAusente({ ausente, editavel, onToggle }: CelulaAusenteProps) {
  if (!editavel) {
    return (
      <td className="border-r border-b text-center p-2 min-w-[100px]">
        {ausente && (
          <Badge variant="destructive" className="text-xs">
            <Ban className="w-3 h-3 mr-1" />
            Ausente
          </Badge>
        )}
      </td>
    );
  }

  return (
    <td className="border-r border-b text-center p-2 min-w-[100px]">
      <Button
        size="sm"
        variant={ausente ? 'destructive' : 'outline'}
        onClick={onToggle}
        className="text-xs h-7"
      >
        <Ban className="w-3 h-3 mr-1" />
        {ausente ? 'Ausente' : 'Marcar Ausente'}
      </Button>
    </td>
  );
}

interface PlanilhaHorariosProps {
  dados: ListarHorariosResponse;
  podeEditar: boolean;
  idMilitarLogado: string;
  onSalvar: (data: SalvarMarcacoesRequest) => Promise<boolean>;
  salvando: boolean;
}

export function PlanilhaHorarios({
  dados,
  podeEditar,
  idMilitarLogado,
  onSalvar,
  salvando,
}: PlanilhaHorariosProps) {
  const { toast } = useToast();
  const [diaSelecionado, setDiaSelecionado] = useState<DiaSemana>(getDiaAtual());
  const [editando, setEditando] = useState(false);
  const [horariosSelecionados, setHorariosSelecionados] = useState<string[]>([]);
  const [ausenteSelecionado, setAusenteSelecionado] = useState(false);

  // Obter data de referência do dia selecionado
  const getDataReferencia = (): string => {
    const marcacaoExemplo = dados.oficiais[0]?.marcacoes[diaSelecionado];
    return marcacaoExemplo?.data || '';
  };

  const dataReferencia = getDataReferencia();
  const marcacaoInfo = dados.oficiais[0]?.marcacoes[diaSelecionado];
  const bloqueado = marcacaoInfo?.bloqueado || false;

  // Função para normalizar horários (remover segundos e duplicatas)
  const normalizarHorarios = (horarios: string[]): string[] => {
    const normalizados = horarios.map(h => {
      // Se tem formato HH:MM:SS, converter para HH:MM
      const parts = h.split(':');
      return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : h;
    });
    // Remover duplicatas
    return Array.from(new Set(normalizados)).sort();
  };

  // Carregar dados do usuário quando mudar dia ou quando receber novos dados
  useEffect(() => {
    const meusDados = dados.oficiais.find(o => o.id_militar === idMilitarLogado);
    if (meusDados) {
      const marcacao = meusDados.marcacoes[diaSelecionado];
      setHorariosSelecionados(normalizarHorarios(marcacao?.horarios || []));
      setAusenteSelecionado(marcacao?.ausente || false);
    }
    setEditando(false);
  }, [diaSelecionado, dados, idMilitarLogado]);

  const handleToggleHorario = (horario: string) => {
    if (!editando || bloqueado) return;
    setHorariosSelecionados(prev =>
      prev.includes(horario)
        ? prev.filter(h => h !== horario)
        : [...prev, horario].sort()
    );
    setAusenteSelecionado(false); // Desmarcar ausente ao marcar horário
  };

  const handleToggleAusente = () => {
    if (!editando || bloqueado) return;
    const novoValor = !ausenteSelecionado;
    setAusenteSelecionado(novoValor);
    if (novoValor) {
      setHorariosSelecionados([]); // Limpar horários ao marcar ausente
    }
  };

  const handleSalvar = async () => {
    if (bloqueado) {
      toast({
        title: 'Dia bloqueado',
        description: 'Não é mais possível editar este dia.',
        variant: 'destructive',
      });
      return;
    }

    // Normalizar horários antes de salvar (remover duplicatas e garantir formato HH:MM)
    const horariosNormalizados = normalizarHorarios(horariosSelecionados);

    const sucesso = await onSalvar({
      dataReferencia,
      horarios: ausenteSelecionado ? [] : horariosNormalizados,
      marcadoAusente: ausenteSelecionado,
    });

    if (sucesso) {
      setEditando(false);
      toast({
        title: 'Sucesso!',
        description: 'Seus horários foram salvos.',
      });
    }
  };

  const handleCancelar = () => {
    // Restaurar valores originais
    const meusDados = dados.oficiais.find(o => o.id_militar === idMilitarLogado);
    if (meusDados) {
      const marcacao = meusDados.marcacoes[diaSelecionado];
      setHorariosSelecionados(normalizarHorarios(marcacao?.horarios || []));
      setAusenteSelecionado(marcacao?.ausente || false);
    }
    setEditando(false);
  };

  // Calcular totalizadores por horário
  const calcularTotalizadores = () => {
    const totais: { [horario: string]: number } = {};
    HORARIOS_DISPONIVEIS.forEach(horario => {
      totais[horario] = dados.oficiais.filter(oficial => {
        const marcacao = oficial.marcacoes[diaSelecionado];
        // Normalizar horários para comparação correta
        const horariosNormalizados = normalizarHorarios(marcacao?.horarios || []);
        return !marcacao?.ausente && horariosNormalizados.includes(horario);
      }).length;
    });
    return totais;
  };

  const totalizadores = calcularTotalizadores();
  const totalAusentes = dados.oficiais.filter(o => o.marcacoes[diaSelecionado]?.ausente).length;

  const diaInfo = DIAS_SEMANA.find(d => d.key === diaSelecionado);

  return (
    <Card>
      {/* Header com seletor de dia */}
      <div className="p-4 border-b bg-muted/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Calendar className="w-5 h-5 text-primary hidden sm:block" />
            <div className="w-full sm:w-auto">
              <label className="text-sm font-medium text-muted-foreground block mb-1">
                Selecione o dia da semana:
              </label>
              <Select value={diaSelecionado} onValueChange={(v) => setDiaSelecionado(v as DiaSemana)}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIAS_SEMANA.map(dia => (
                    <SelectItem key={dia.key} value={dia.key}>
                      {dia.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {bloqueado && (
              <Badge variant="secondary" className="self-start sm:self-auto">
                <Lock className="w-3 h-3 mr-1" />
                Bloqueado
              </Badge>
            )}
          </div>

          {podeEditar && !bloqueado && (
            <div className="flex gap-2 w-full sm:w-auto">
              {!editando ? (
                <Button onClick={() => setEditando(true)} disabled={salvando} className="w-full sm:w-auto">
                  Editar Meus Horários
                </Button>
              ) : (
                <>
                  <Button onClick={handleCancelar} variant="outline" disabled={salvando} className="flex-1 sm:flex-none">
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button onClick={handleSalvar} disabled={salvando} className="flex-1 sm:flex-none">
                    <Save className="w-4 h-4 mr-2" />
                    {salvando ? 'Salvando...' : 'Salvar'}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {dataReferencia && (
          <p className="text-sm text-muted-foreground mt-2">
            Data: {new Date(dataReferencia + 'T12:00:00').toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })}
          </p>
        )}
      </div>

      {/* Grid de horários */}
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-muted/50">
              <th className="border-r border-b p-2 text-left text-sm font-semibold sticky left-0 top-0 bg-muted z-30 min-w-[150px]">
                Oficial
              </th>
              {HORARIOS_DISPONIVEIS.map(horario => (
                <th key={horario} className="border-r border-b p-2 text-center text-xs font-semibold sticky top-0 bg-muted z-20 min-w-[50px]">
                  {horario}
                </th>
              ))}
              <th className="border-b p-2 text-center text-sm font-semibold sticky top-0 bg-muted z-20 min-w-[100px]">
                Ausente
              </th>
            </tr>
          </thead>
          <tbody>
            {dados.oficiais.map(oficial => {
              const ehMeuHorario = oficial.id_militar === idMilitarLogado;
              const marcacao = oficial.marcacoes[diaSelecionado];

              // Normalizar horários do backend para comparação
              const horariosBackend = normalizarHorarios(marcacao?.horarios || []);
              const horariosOficial = editando && ehMeuHorario ? horariosSelecionados : horariosBackend;
              const ausenteOficial = editando && ehMeuHorario ? ausenteSelecionado : (marcacao?.ausente || false);

              return (
                <tr
                  key={oficial.id_militar}
                  className={`hover:bg-muted/20 ${ehMeuHorario ? 'bg-primary/5 border-l-4 border-l-primary' : ''}`}
                >
                  <td className={`border-r border-b p-2 sticky left-0 z-10 ${ehMeuHorario ? 'bg-primary/5' : 'bg-background'}`}>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{oficial.nick}</span>
                        {ehMeuHorario && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0">
                            Você
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {oficial.patente.patente}
                      </span>
                    </div>
                  </td>
                  {HORARIOS_DISPONIVEIS.map(horario => (
                    <CelulaHorario
                      key={horario}
                      horario={horario}
                      marcado={horariosOficial.includes(horario)}
                      editavel={ehMeuHorario && editando && !bloqueado}
                      onToggle={() => handleToggleHorario(horario)}
                    />
                  ))}
                  <CelulaAusente
                    ausente={ausenteOficial}
                    editavel={ehMeuHorario && editando && !bloqueado}
                    onToggle={handleToggleAusente}
                  />
                </tr>
              );
            })}

            {/* Linha de totalizadores */}
            <tr className="bg-muted/50 font-semibold border-t-2">
              <td className="border-r p-2 sticky left-0 bg-muted/50 z-10">
                <div className="flex items-center gap-2">
                  <span className="text-sm">Total de Oficiais</span>
                </div>
              </td>
              {HORARIOS_DISPONIVEIS.map(horario => (
                <td key={horario} className="border-r text-center p-2">
                  <Badge
                    variant={totalizadores[horario] > 0 ? 'default' : 'outline'}
                    className="text-xs"
                  >
                    {totalizadores[horario]}
                  </Badge>
                </td>
              ))}
              <td className="text-center p-2">
                <Badge variant="destructive" className="text-xs">
                  {totalAusentes}
                </Badge>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {dados.oficiais.length === 0 && (
        <div className="py-12 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Nenhum oficial encontrado</p>
        </div>
      )}
    </Card>
  );
}
